import { useState, useEffect } from 'react';
import { checkElearningPaymentStatus } from '../services/elearningPaymentService';

interface UseElearningOrderStatusProps {
  orderId: string;
  onSuccess?: () => void;
  onError?: (error: Error) => void;
  pollingInterval?: number;
}

/**
 * Hook pour suivre le statut d'une commande e-learning
 * Effectue un polling régulier pour vérifier si le paiement a été complété
 */
export const useElearningOrderStatus = ({
  orderId,
  onSuccess,
  onError,
  pollingInterval = 5000 // Par défaut, vérifier toutes les 5 secondes
}: UseElearningOrderStatusProps) => {
  const [status, setStatus] = useState<'pending' | 'success' | 'failed'>('pending');
  const [isPolling, setIsPolling] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!orderId) {
      setError(new Error('ID de commande non fourni'));
      return;
    }

    let timeoutId: NodeJS.Timeout;
    let isMounted = true;

    const checkStatus = async () => {
      try {
        console.log(`[useElearningOrderStatus] Vérification du statut pour la commande ${orderId}`);
        const response = await checkElearningPaymentStatus(orderId);
        
        console.log(`[useElearningOrderStatus] Réponse reçue:`, response);
        
        if (!isMounted) return;
        
        // Vérifier si le paiement est réussi
        if (response.status === 'success' || response.status === 'SUCCESSFUL') {
          console.log(`[useElearningOrderStatus] Paiement réussi pour la commande ${orderId}`);
          setStatus('success');
          setIsPolling(false);
          
          if (onSuccess) {
            onSuccess();
          }
        } else if (response.status === 'failed' || response.status === 'FAILED') {
          console.log(`[useElearningOrderStatus] Paiement échoué pour la commande ${orderId}`);
          setStatus('failed');
          setIsPolling(false);
          
          if (onError) {
            onError(new Error('Le paiement a échoué'));
          }
        } else {
          // Continuer le polling
          if (isMounted && isPolling) {
            console.log(`[useElearningOrderStatus] Paiement en attente, prochaine vérification dans ${pollingInterval}ms`);
            timeoutId = setTimeout(checkStatus, pollingInterval);
          }
        }
      } catch (err) {
        console.error(`[useElearningOrderStatus] Erreur lors de la vérification du statut:`, err);
        
        if (!isMounted) return;
        
        const error = err instanceof Error ? err : new Error('Erreur inconnue');
        setError(error);
        
        // Continuer le polling malgré l'erreur
        if (isMounted && isPolling) {
          console.log(`[useElearningOrderStatus] Erreur lors de la vérification, nouvelle tentative dans ${pollingInterval}ms`);
          timeoutId = setTimeout(checkStatus, pollingInterval);
        }
      }
    };

    // Démarrer le polling
    checkStatus();

    // Nettoyage
    return () => {
      isMounted = false;
      setIsPolling(false);
      clearTimeout(timeoutId);
    };
  }, [orderId, onSuccess, onError, pollingInterval, isPolling]);

  return {
    status,
    error,
    isPolling,
    stopPolling: () => setIsPolling(false),
    startPolling: () => setIsPolling(true)
  };
};
